import {
  ModelPsAgreementRequests,
  ModelPsAgreementTemplate,
  ModelPsFlowPages,
} from '../../models/fireModels/types'
import { AgreementFlowDefinitionValueTemplateUserDefineItem } from '../AgreementFlowDefinitionInput/AgreementFlowDefinitionInputService'

/**
 * # サービス "AgreementFlowService"
 *
 * 同意フローに関するサービス, 基本的には 表示と, ユーザーの入力を管理する (のみ)
 *
 * ## 責任範囲
 * - 同意フローのページの表示内容を提供する
 * - ユーザーが入力した値を保持・表示する
 * - 同意の最後まで到達したら 与えられた Callback を実行する
 *
 */
export class AgreementFlowService implements AgreementFlowServiceConstructor {
  public readonly agreementRequestId: AgreementFlowServiceConstructor['agreementRequestId']
  /**
   * フローページにおいて、表示上のページと、それに紐づく変数を保持
   */
  public readonly flowPagesAndVariables: AgreementFlowServiceConstructor['flowPagesAndVariables']
  /**
   * ユーザーが入力した値を保持
   */
  public userInputtedValues: AgreementFlowServiceConstructor['userInputtedValues']
  /**
   * ユーザーが入力した値に対するエラーメッセージを保持
   */
  public userInputErrors: Record<string, any> = {}
  /**
   * ユーザー入力をロックするかどうか (boolean)
   * - すでに保存されているフローを表示する場合 などで true にする
   */
  public readonly lockUserInputs: AgreementFlowServiceConstructor['lockUserInputs']
  /**
   * 同意が完了したときに呼び出される関数
   */
  public readonly callbackOnAgreementCompleted: AgreementFlowServiceConstructor['callbackOnAgreementCompleted']
  /**
   * 同意フロー全体を通じて適用される変数
   */
  public readonly agreementVariables: AgreementFlowServiceConstructor['agreementVariables']
  // 現在のページの index, getter / setter で範囲外の値が入らないように制御
  private _currentPageIndex: AgreementFlowServiceConstructor['currentPageIndex'] = 0
  get currentPageIndex() {
    return this._currentPageIndex
  }
  set currentPageIndex(index: number) {
    if (index < 0) {
      this._currentPageIndex = 0
    } else if (index >= this.totalPagesCount) {
      this._currentPageIndex = this.totalPagesCount - 1
    } else {
      this._currentPageIndex = index
    }
  }
  // 合計ページ数, 初期化時に計算
  public readonly totalPagesCount: number
  // Component 上で利用しやすいように, flowPages をバラしつつ, flowVariables を紐づけて保持
  public readonly flowPageFlattenedArrayWithVariables: {
    flowPage: AgreementFlowServiceConstructor['flowPagesAndVariables'][0]['flowPages'][0]
    flowVariables: AgreementFlowServiceConstructor['flowPagesAndVariables'][0]['flowVariables']
  }[]

  public hasSubmitCompleted: boolean = false

  public readonly agreementRequest?: ModelPsAgreementRequests | null

  public readonly inputHasErrorClass = 'afs--input-has-error'

  public uiState:
    | 'default'
    | 'submit-modal-open'
    | 'submit-running'
    | 'submit-completed' = 'default'

  constructor(args: AgreementFlowServiceConstructor) {
    this.agreementRequestId = args.agreementRequestId
    this.flowPagesAndVariables = args.flowPagesAndVariables
    this.userInputtedValues = args.userInputtedValues || {}
    this.lockUserInputs = args.lockUserInputs || false
    this.callbackOnAgreementCompleted = args.callbackOnAgreementCompleted
    this.agreementVariables = args.agreementVariables || {}
    this._currentPageIndex = args.currentPageIndex || 0
    // 初期化 calculates
    this.totalPagesCount = this.flowPagesAndVariables.reduce(
      (acc, item) => acc + item.flowPages.length,
      0,
    )
    this.flowPageFlattenedArrayWithVariables = this.flowPagesAndVariables.reduce(
      (pagesArray, { flowPages, flowVariables }) => {
        flowPages.forEach(flowPage => {
          pagesArray.push({ flowPage, flowVariables })
        })
        return pagesArray
      },
      [] as AgreementFlowService['flowPageFlattenedArrayWithVariables'],
    )
    // @ts-ignore
    this.agreementRequest = args.agreementVariables.agreementRequest || null
  }

  get isLastPage() {
    return this.currentPageIndex === this.totalPagesCount - 1
  }

  /**
   * 同意内容を送信する
   */
  async runSubmit() {
    this.uiState = 'submit-running'
    $core.$loading.start('', 'overlay')
    try {
      await this.callbackOnAgreementCompleted(this)
      this.hasSubmitCompleted = true
      this.uiState = 'submit-completed'
    } catch (e) {
      console.error(e)
      this.uiState = 'submit-modal-open'
    } finally {
      $core.$loading.end()
    }
  }
}

export interface AgreementFlowServiceConstructor {
  agreementRequestId?: number
  flowPagesAndVariables: {
    flowPages: ModelPsFlowPages[]
    flowVariables: Record<string, AgreementFlowDefinitionValueTemplateUserDefineItem> // key-value pair
  }[]
  userInputtedValues: Record<string, any> // key-value pair
  lockUserInputs: boolean // すでに保存されている場合 などに true にする
  callbackOnAgreementCompleted: (service: AgreementFlowService) => Promise<void> // 同意が finish したときに呼び出す関数
  agreementVariables: {
    agreementRequest?: Partial<ModelPsAgreementRequests>
    agreementTemplate?: Partial<ModelPsAgreementTemplate>
    [key: string]: any
  } // key-value pair, 同意フロー全体を通じて適用される変数
  currentPageIndex: number // 現在のページの index, 0-indexed, デフォルトは 0
}
